/* SPDX-License-Identifier: AGPL-3.0-or-later */

import { Component, OnInit } from '@angular/core';
import { KoStandingsEndpointService, TeamDto, KoStandingDto } from '@api';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { KnockoutPhaseTreeNodeComponent } from '../knockout-phase-tree-node/knockout-phase-tree-node.component';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

type RoundStatus = 'final-winner' | 'winner' | 'loser' | 'indeterminate';

@Component({
  selector: 'app-infoscreen-knockout-phase-tree',
  standalone: true,
  imports: [
    KnockoutPhaseTreeNodeComponent,
    MatProgressSpinnerModule,
    MatCardModule,
    MatButton,
    MatIcon,
  ],
  templateUrl: './infoscreen-knockout-phase-tree.component.html',
  styleUrl: './infoscreen-knockout-phase-tree.component.scss',
})
export class InfoscreenKnockoutPhaseTreeComponent implements OnInit {
  tree?: KoStandingDto;

  isInitialLoading = true;
  showErrorMessage = false;
  noStandingsFound = false;

  refreshInterval: number | undefined;

  constructor(
    private koStandingsEndpoint: KoStandingsEndpointService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.loadData();
    this.refreshInterval = window.setInterval(() => this.loadData(), 3000);
  }

  loadData = () => {
    this.route.params.subscribe(params =>
      this.koStandingsEndpoint.getKoStandingsTree(Number.parseInt(params.tournamentId)).subscribe({
        next: value => {
          this.isInitialLoading = false;
          this.showErrorMessage = false;
          this.noStandingsFound = false;

          this.tree = value;
        },
        error: er => {
          if (er instanceof HttpErrorResponse) {
            this.noStandingsFound = er.status === 404;
          }
          this.isInitialLoading = false;
          this.showErrorMessage = true;
        },
      }),
    );
  };

  teamHasPreviousWonRound = (matchId: number): RoundStatus => {
    if (!this.tree) {
      return 'indeterminate';
    }
    if (this.tree.id === matchId && this.tree.team) {
      return 'final-winner';
    }

    const findNodeById = (node: KoStandingDto, parentTeam?: TeamDto): RoundStatus[] =>
      node.id === matchId && !parentTeam
        ? ['indeterminate']
        : node.id === matchId && parentTeam && node.team && parentTeam.id === node.team.id
          ? ['winner']
          : node.id === matchId && parentTeam && node.team && parentTeam.id !== node.team.id
            ? ['loser']
            : (node.preceedingStandings?.flatMap(n => findNodeById(n, node.team)) ?? ['loser']);

    const [targetNode] = findNodeById(this.tree);

    return targetNode;
  };

  teamHasPickedUpDrinksForMatch(matchId: number | undefined, team: TeamDto | undefined): boolean {
    if (this.tree === undefined || matchId === undefined || team?.id === undefined) {
      return false;
    }

    const [node] = this.findNode(this.tree, matchId);
    return node?.drinksCollected ?? false;
  }

  findNode = (node: KoStandingDto, matchId: number): KoStandingDto[] =>
    node.id === matchId
      ? [node]
      : (node.preceedingStandings?.flatMap(n => this.findNode(n, matchId)) ?? []);

  isBeginnerNode(preceedingStandings: KoStandingDto[] | undefined): boolean {
    return !preceedingStandings || preceedingStandings.length === 0;
  }
}
